import { MAX_RAYS } from "../../vfx/definition.ts";

/**
 * The burst's art (P2.4-T41), drawn in code as pixels and baked into one atlas: nothing in it comes from a pack or an
 * image file. Every frame is white and takes its colour from the tint it is drawn with (the tones of a vfx, ADR-134
 * decision 4); the aura is a dithered ellipse. Every
 * frame is a whole number of art pixels, drawn nearest-filtered at a whole number of device pixels an art pixel.
 */

/**
 * The texture key the atlas is added under.
 */
export const BURST_TEXTURE = "vfx";

/**
 * The name of the frame of one opaque white pixel, which the pillar, the sign's box and the edge glow stretch.
 */
export const PIXEL_FRAME = "pixel";

/**
 * The name of the aura's frame.
 */
export const AURA_FRAME = "aura";

/**
 * The smallest and the largest ring radius, in art pixels, the atlas holds a frame for.
 */
export const MIN_RING = 2;
export const MAX_RING = 44;

/**
 * The fewest and the most dots a ray has: three at the start of the burst, seven at its end.
 */
export const MIN_RAY_DOTS = 3;
export const MAX_RAY_DOTS = 7;

/**
 * How flat the rings and the rays lie, against the ground's perspective: a ring is 0.4 as tall as wide and a ray's
 * downward run is 0.7 of its across run.
 */
const RING_SQUASH = 0.4;
const RAY_SQUASH = 0.7;

const WHITE = [255, 255, 255] as const;
const SPARK_SIZE = 5;
const SPARK_MIDDLE = 2;
const AURA_WIDTH = 29;
const AURA_HEIGHT = 45;
const ATLAS_WIDTH = 512;

/**
 * The name of a ring's frame.
 *
 * @param radius The radius in art pixels, `MIN_RING` to `MAX_RING`.
 * @returns The name.
 */
export function ringFrame(radius: number): string {
  return `ring-${radius}`;
}

/**
 * The name of a ray's frame.
 *
 * @param angle Which of the `MAX_RAYS` angles, 0 to 11.
 * @param dots How many dots it has, `MIN_RAY_DOTS` to `MAX_RAY_DOTS`.
 * @returns The name.
 */
export function rayFrame(angle: number, dots: number): string {
  return `ray-${angle}-${dots}`;
}

/**
 * The name of a spark's frame.
 *
 * @param arm The length of its plus's arms: 2, 1, or 0 for a single dot.
 * @returns The name.
 */
export function sparkFrame(arm: number): string {
  return `spark-${arm}`;
}

/**
 * One frame of the atlas: its name and where it stands, in pixels.
 */
export interface AtlasFrame {
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * The baked atlas: its size, its pixels as RGBA, and its frames.
 */
export interface Atlas {
  readonly width: number;
  readonly height: number;
  readonly pixels: Uint8ClampedArray;
  readonly frames: readonly AtlasFrame[];
}

/**
 * A frame before it is placed: its name, its size and the pixels it lights, each with its colour.
 */
interface Piece {
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly lit: readonly { readonly x: number; readonly y: number; readonly colour: readonly [number, number, number] | readonly number[] }[];
}

/**
 * A ring's pixels: one pixel thick, every angle sampled so no gap is left.
 *
 * @param radius The radius in art pixels.
 * @returns The ring's frame.
 */
function ringPiece(radius: number): Piece {
  const half = Math.round(radius * RING_SQUASH);
  const seen = new Set<number>();
  const lit: { x: number; y: number; colour: readonly number[] }[] = [];
  for (let degrees = 0; degrees < 360; degrees += 1) {
    const angle = (degrees * Math.PI) / 180;
    const x = radius + Math.round(radius * Math.cos(angle));
    const y = half + Math.round(radius * RING_SQUASH * Math.sin(angle));
    const key = y * (2 * radius + 1) + x;
    if (!seen.has(key)) {
      seen.add(key);
      lit.push({ x, y, colour: WHITE });
    }
  }
  return { name: ringFrame(radius), width: 2 * radius + 1, height: 2 * half + 1, lit };
}

/**
 * A ray's pixels: dots two pixels apart along its angle from the middle of the frame.
 *
 * @param angle Which angle, 0 to `MAX_RAYS - 1`, in steps of 30 degrees.
 * @param dots How many dots.
 * @returns The ray's frame.
 */
function rayPiece(angle: number, dots: number): Piece {
  const reach = 2 * (dots - 1);
  const halfHeight = Math.ceil(reach * RAY_SQUASH);
  const radians = (angle * 2 * Math.PI) / MAX_RAYS;
  const lit: { x: number; y: number; colour: readonly number[] }[] = [];
  for (let dot = 0; dot < dots; dot += 1) {
    const s = 2 * dot;
    lit.push({ x: reach + Math.round(s * Math.cos(radians)), y: halfHeight + Math.round(s * RAY_SQUASH * Math.sin(radians)), colour: WHITE });
  }
  return { name: rayFrame(angle, dots), width: 2 * reach + 1, height: 2 * halfHeight + 1, lit };
}

/**
 * A spark's pixels: a centre and four arms.
 *
 * @param arm The arms' length, 0 to 2.
 * @returns The spark's frame.
 */
function sparkPiece(arm: number): Piece {
  const lit: { x: number; y: number; colour: readonly number[] }[] = [{ x: SPARK_MIDDLE, y: SPARK_MIDDLE, colour: WHITE }];
  for (let step = 1; step <= arm; step += 1) {
    lit.push({ x: SPARK_MIDDLE + step, y: SPARK_MIDDLE, colour: WHITE }, { x: SPARK_MIDDLE - step, y: SPARK_MIDDLE, colour: WHITE }, { x: SPARK_MIDDLE, y: SPARK_MIDDLE + step, colour: WHITE }, { x: SPARK_MIDDLE, y: SPARK_MIDDLE - step, colour: WHITE });
  }
  return { name: sparkFrame(arm), width: SPARK_SIZE, height: SPARK_SIZE, lit };
}

/**
 * The aura's pixels: every other pixel inside an ellipse taller than wide, so a character stands in a haze that shows
 * the character through it.
 *
 * @returns The aura's frame.
 */
function auraPiece(): Piece {
  const lit: { x: number; y: number; colour: readonly number[] }[] = [];
  const cx = (AURA_WIDTH - 1) / 2;
  const cy = (AURA_HEIGHT - 1) / 2;
  for (let y = 0; y < AURA_HEIGHT; y += 1) {
    for (let x = 0; x < AURA_WIDTH; x += 1) {
      const inside = ((x - cx) / (AURA_WIDTH / 2)) ** 2 + ((y - cy) / (AURA_HEIGHT / 2)) ** 2 <= 1;
      if (inside && (x + y) % 2 === 0) {
        lit.push({ x, y, colour: WHITE });
      }
    }
  }
  return { name: AURA_FRAME, width: AURA_WIDTH, height: AURA_HEIGHT, lit };
}

/**
 * Every frame of the atlas, before placing.
 *
 * @returns The frames.
 */
function pieces(): Piece[] {
  const all: Piece[] = [{ name: PIXEL_FRAME, width: 1, height: 1, lit: [{ x: 0, y: 0, colour: WHITE }] }, auraPiece()];
  for (let radius = MIN_RING; radius <= MAX_RING; radius += 1) {
    all.push(ringPiece(radius));
  }
  for (let angle = 0; angle < MAX_RAYS; angle += 1) {
    for (let dots = MIN_RAY_DOTS; dots <= MAX_RAY_DOTS; dots += 1) {
      all.push(rayPiece(angle, dots));
    }
  }
  for (let arm = 0; arm <= 2; arm += 1) {
    all.push(sparkPiece(arm));
  }
  return all;
}

/**
 * Draws every frame and packs them in rows into one atlas, a pixel apart so a nearest-filtered frame never reads its
 * neighbour.
 *
 * @returns The atlas.
 */
export function bakeAtlas(): Atlas {
  const all = pieces();
  const placed: { piece: Piece; x: number; y: number }[] = [];
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  for (const piece of all) {
    if (x + piece.width > ATLAS_WIDTH) {
      x = 0;
      y += rowHeight + 1;
      rowHeight = 0;
    }
    placed.push({ piece, x, y });
    x += piece.width + 1;
    rowHeight = Math.max(rowHeight, piece.height);
  }
  const height = y + rowHeight;
  const pixels = new Uint8ClampedArray(ATLAS_WIDTH * height * 4);
  for (const { piece, x: left, y: top } of placed) {
    for (const dot of piece.lit) {
      const at = ((top + dot.y) * ATLAS_WIDTH + left + dot.x) * 4;
      pixels[at] = dot.colour[0] as number;
      pixels[at + 1] = dot.colour[1] as number;
      pixels[at + 2] = dot.colour[2] as number;
      pixels[at + 3] = 255;
    }
  }
  return { width: ATLAS_WIDTH, height, pixels, frames: placed.map(({ piece, x: left, y: top }) => ({ name: piece.name, x: left, y: top, width: piece.width, height: piece.height })) };
}
